import { useChannelTalk } from '@repo/channel-talk';
import { getCookieData } from '@repo/common-utils';
import { useWorkingPlatform } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { useToast } from '@zenterprise-inc/ui';
import { lowerCase } from 'es-toolkit';
import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { CertType } from '@/__generated__/simpleAuthRequestTokenMutation.graphql';
import Layout from '@/components/layout/Layout';
import { getIndisSearchRefundData } from '@/components/tax-refund/common/data/indis-search-refund-data';
import { CHECK_DECISION_ACTION_CODE_COOKIE_KEY } from '@/components/tax-refund/common/data/result-refund-data';
import { openKakaoTalk } from '@/components/tax-refund/common/utils/openKakaoTalk';
import { useSimpleAuthDialog } from '@/components/tax-refund/simple-auth/confirm/Dialogs';
import { NaverAppInstallCheckDrawer } from '@/components/tax-refund/simple-auth/input/NaverAppInstallCheckDrawer';
import { SimpleAuthInputForm } from '@/components/tax-refund/simple-auth/input/SimpleAuthInputForm';
import { SUPPORT_BOT_LINK } from '@/lib/constants/link';
import { COMMON_PATHS } from '@/lib/constants/paths';
import useSaveRefundActionLog from '@/lib/hooks/api/mutation/use-save-refund-action-log';
import { useExitService } from '@/lib/hooks/use-exit-service';
import { useRequestSimpleAuthToken } from '@/lib/hooks/use-hometax-simple-auth';
import { useFromTypeStaticData } from '@/lib/hooks/use-simple-auth-from-type';
import { simpleAuthCertTypeAtom, simpleAuthCredentialAtom, simpleAuthFromTypeAtom, simpleAuthUserIdentityAtom } from '@/lib/stores/simple-auth';
import { SimpleAuthCertificateType, SimpleAuthFromType } from '@/lib/types/simple-auth';

interface UserInfoType {
  name: string;
  phone: string;
  birthday: string;
}

export type SubmitHandler = (certType: CertType, userInfo: UserInfoType) => void;

export const shouldSendActionLog = (fromType: SimpleAuthFromType, actionCode: string | undefined) => {
  return ['decision', 'alimtalk'].includes(fromType) && !!actionCode;
};

export const SimpleAuthInputContainer = () => {
  const router = useRouter();
  const { workingPlatform } = useWorkingPlatform();
  const toast = useToast();
  const { showDialogExitConfirm } = useSimpleAuthDialog();
  const simpleAuthFromType = useAtomValue(simpleAuthFromTypeAtom);
  const setCertType = useSetAtom(simpleAuthCertTypeAtom);
  const setSimpleAuthUserIdentity = useSetAtom(simpleAuthUserIdentityAtom);
  const setSimpleAuthCredential = useSetAtom(simpleAuthCredentialAtom);
  const [naverAuthDrawerOpen, setNaverAuthDrawerOpen] = useState(false);
  const { requestSimpleAuthToken } = useRequestSimpleAuthToken();
  const { commit: commitActionLog } = useSaveRefundActionLog();
  const { sendClickEvent, sendViewEvent } = useUserEventLogger();
  const [isLoading, setIsLoading] = useState(false);
  const { openChannelTalk } = useChannelTalk();
  const { exitService } = useExitService();
  const { getInputPageEvents } = useFromTypeStaticData(simpleAuthFromType);
  const events = getInputPageEvents();

  sendViewEvent(events.view);

  const handleSubmit: SubmitHandler = (certType, userInfo) => {
    setIsLoading(true);
    sendClickEvent(events.clickCta);
    requestSimpleAuthToken({
      variables: {
        name: userInfo.name,
        phone: userInfo.phone,
        birthday: userInfo.birthday,
        certType
      },
      onCompleted: (res, errors) => {
        // 1. 서버 통신 에러
        if (errors) {
          console.error(errors);
          showErrorToast('알 수 없는 오류입니다.');
          return;
        }
        // 2. 네이버 인증 또는 서버 정의된 에러
        const errorMessage = res.hometaxSimpleAuth.errors?.message;
        if (errorMessage === '네이버로 인증하려면 네이버 앱 설치가 필요해요.') {
          setNaverAuthDrawerOpen(true);
          setIsLoading(false);
          return;
        }
        if (errorMessage) {
          showErrorToast(errorMessage);
          return;
        }
        const { result, reqTxId, cxId, token } = res.hometaxSimpleAuth ?? {};
        if (!result || !reqTxId || !cxId || !token) {
          showErrorToast('잠시 후 다시 시도해주세요.');
          return;
        }
        // 3. 다음 단계로 이동하기 전 필요한 정보 저장
        setCertType(lowerCase(certType) as SimpleAuthCertificateType);
        setSimpleAuthUserIdentity(userInfo);
        setSimpleAuthCredential({ reqTxId, token, cxId });
        // 4. 액션코드 전송
        sendActionLog();
        // 5. 단계 이동
        setIsLoading(false);
        router.push({
          pathname: COMMON_PATHS.간편인증_인증확인,
          query: router.query?.actionCode ? { actionCode: router.query.actionCode } : undefined
        });
        // 6. 카카오톡 앱 연결조건에 부합하는 경우 앱으로 이동
        if (certType === 'Kakao' && cxId) openKakaoTalk(cxId);
      },
      onError: () => {
        showErrorToast('알 수 없는 오류입니다.');
      }
    });
  };

  const sendActionLog = () => {
    const refundData = getIndisSearchRefundData();
    const actionCode = router.query?.actionCode ?? getCookieData(CHECK_DECISION_ACTION_CODE_COOKIE_KEY);

    if (shouldSendActionLog(simpleAuthFromType, actionCode)) {
      commitActionLog({
        variables: {
          actionCode,
          refundId: refundData?.refundId,
          actionStatus: '간편인증요청'
        }
      });
    }
  };

  const showErrorToast = (message: string) => {
    toast({ content: message, icon: 'error', iconColor: 'red.600' });
    setIsLoading(false);
  };

  const topNavLeftIcon = workingPlatform === 'kbank' ? 'close' : 'back';

  return (
    <Layout
      leftIcon={{
        icon: topNavLeftIcon,
        onClick: () => {
          if (topNavLeftIcon === 'back') {
            sendClickEvent(events.clickBack);
            router.back();
            return;
          }
          // kbank exit 용
          showDialogExitConfirm({
            onClickPrimary: () => {
              exitService();
            }
          });
        }
      }}
      rightTextButton={{
        label: '문의하기',
        onClick: () => {
          sendClickEvent(events.clickCx);
          openChannelTalk(SUPPORT_BOT_LINK.인증정보입력);
        }
      }}
    >
      <SimpleAuthInputForm onSubmit={handleSubmit} isLoading={isLoading} />
      <NaverAppInstallCheckDrawer
        isVisible={naverAuthDrawerOpen}
        onClose={() => {
          setNaverAuthDrawerOpen(false);
        }}
      />
    </Layout>
  );
};
